import React from "react";
import { usePayment } from "../hooks";
import { useTranslation } from "react-i18next";
import { Box, Typography } from "@mui/material";
import StackList from "./stackList";
import { formatCurrency } from "../utils";
import { displayPhone } from "../utils/displayMask";
import { DEFAULT_PHONE_COUNTRY } from "../constants/phoneCountries";

// The unit is the paid service's own, taken from the service response. Nothing
// on this screen has a pay response yet -- it is the step BEFORE the payment.
const PaymentSummary: React.FC<{ currency?: string }> = ({ currency }) => {
  const { amount, name, selectedMethod, phoneNumber } =
    usePayment();
  const { t } = useTranslation();

  const listData = [
    {
      title: t("payment.form.amount"),
      // ⛔ This row used to be `parseInt(amount) + " FCFA"`. That is not a
      // formatting nicety: it DISPLAYED 100 while the body sent 100.99, so the
      // payer confirmed a different number from the one charged. It also
      // hardcoded a unit nothing had told it. A system-supplied fee is
      // legitimately decimal and must survive unrounded.
      value: formatCurrency(amount, undefined, currency),
    },
    {
      title: t("payment.form.customer_name"),
      value: name,
    },
    {
      // The row holds the mobile number that will be debited, not a customer
      // code. Labelled "Customer Code" it collided with the searched journey,
      // where that label correctly means the customer's business number.
      title: t("payment.form.mobile_number"),
      value: phoneNumber ? displayPhone(phoneNumber, DEFAULT_PHONE_COUNTRY) : "-",
    },
    {
      title: t("payment.payment_method"),
      value: selectedMethod?.name || "-",
    },
    // A commented-out total row stood here carrying the same
    // `parseInt(amount) + " FCFA"` this change removes. Deleted rather than
    // left: uncommenting it would restore the defect, and ServiceChargeSummary
    // beneath already renders the authoritative total.
  ];

  if (!selectedMethod) return null;

  return (
    <Box>
      <Typography sx={{ textAlign: "center", marginBottom: 2 }} variant="h5">
        {t("payment.details")}
      </Typography>

      <Box
        className="space-y-4"
        sx={{
          backgroundColor: "#F5F5F5",
          padding: 2,
          marginBottom: 5,
        }}
      >
        <Box>
          <StackList spacing={2} list={listData} margin={2} />
        </Box>
      </Box>
    </Box>
  );
};

export default PaymentSummary;
